/**
 * Tempo que cada lado da chamada passou em cada nível de qualidade de conexão do LiveKit
 * (`excellent`, `good`, `poor`, `lost`). Vai com a pesquisa de qualidade para separar rede ruim do
 * atendente (`local`) de rede ruim do contato (`remote`). `unknown` (sem medição ainda) não conta.
 *
 * Objeto simples, fora do React: os eventos do LiveKit não devem re-renderizar a janela, e a
 * medição precisa sobreviver ao fim da sala (a pesquisa aparece depois que a mídia desmontou).
 */

export const CONNECTION_LEVELS = Object.freeze(['excellent', 'good', 'poor', 'lost']);

const SIDES = ['local', 'remote'];

const emptySide = () => ({ level: null, since: 0, ms: { excellent: 0, good: 0, poor: 0, lost: 0 } });

/** @param {() => number} [clock] */
export function createConnectionQualityTracker(clock = () => Date.now()) {
    let sides = { local: emptySide(), remote: emptySide() };

    const close = (side, now) => {
        if (side.level) side.ms[side.level] += Math.max(0, now - side.since);
    };

    return {
        /**
         * Novo nível medido para um lado.
         * @param {'local'|'remote'} sideName
         * @param {string} level - valor de `ConnectionQuality` do livekit-client
         */
        record(sideName, level) {
            const side = sides[sideName];
            if (!side) return;
            const now = clock();
            close(side, now);
            side.level = CONNECTION_LEVELS.includes(level) ? level : null;
            side.since = now;
        },

        /** Para de contar (sala desconectada); um `record` depois retoma. */
        pause() {
            const now = clock();
            for (const name of SIDES) {
                close(sides[name], now);
                sides[name].level = null;
            }
        },

        reset() {
            sides = { local: emptySide(), remote: emptySide() };
        },

        /**
         * Segundos por nível de cada lado, ou `null` sem nenhuma medição.
         * @returns {{ local?: Record<string, number>, remote?: Record<string, number> }|null}
         */
        snapshot() {
            const now = clock();
            const result = {};
            for (const name of SIDES) {
                const side = sides[name];
                const ms = { ...side.ms };
                if (side.level) ms[side.level] += Math.max(0, now - side.since);
                const seconds = Object.fromEntries(CONNECTION_LEVELS.map((level) => [level, Math.round(ms[level] / 1000)]));
                if (CONNECTION_LEVELS.some((level) => seconds[level] > 0)) result[name] = seconds;
            }
            return Object.keys(result).length ? result : null;
        },
    };
}
